import toastHandler from "components/UI/toastHandler/toastHandler";
import StripeDisconnectModal from "components/UI/modals/stripe-disconnect-modal/StripeDisconnectModal";
import { useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import Spinner from "components/UI/Spinner.js";
import * as actions from "../../../_redux/dashboard/dashboardActions";
import { useDashboardUIContext } from "../../DashboardUIContext";
import { useHistory } from "react-router-dom";
import { setAuthStatus } from "modules/landingPage/_redux/landingPageActions";


export default function TransferAmountModal({
  show,
  onHide,
  setShowPaymentInformationModal,
  amountInfoMail,
  isLoadingStripeInfo = false,
}) {
  const [error, setError] = useState();
  const [isLoading, setIsloading] = useState(false);
  const [transferAmountValue, setTransferAmountValue] = useState(0);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isSettingUpStripe, setIsSettingUpStripe] = useState(false);
  const [isLoadingStripeDashboard, setIsLoadingStripeDashboard] = useState(false);

  const dispatch = useDispatch();
  const history = useHistory();
  const toastId = history.location.pathname;

  const dashboardPageUIContext = useDashboardUIContext();
  const dashboardPageUIProps = useMemo(() => {
    return {
      queryParams: dashboardPageUIContext.transactionsListQueryParams,
    };
  }, [dashboardPageUIContext]);

  const { currentUser, authStatus, stripeAccountInfo } = useSelector(
    (state) => ({
      currentUser: state?.auth?.user,
      authStatus: state?.landingPage?.authStatus,
      stripeAccountInfo: state?.dashboard?.stripeAccountInfo
    }),
    shallowEqual
  );

  let pattern = new RegExp('^(([0-9]{1,7})(\\.[0-9]{1})?)$');

  const validationSchema = Yup.object({
    transfer_amount: Yup.number().moreThan(0, "Amount should be greater than zero").test('value', 'Invalid amount', val => pattern.test(val)),
  });

  const onTransferAmountSuccess = () => {
    setIsloading(false);
    toastHandler("Amount transferred successfully", "success", toastId);
    dispatch(actions.getAmountInfo());
    dispatch(actions.getTransactions({...dashboardPageUIProps.queryParams, all_dates: 1}));
    onHide();
    dispatch(setAuthStatus(false));
  };

  const handleModalClose = () => {
    onHide();
    history.push("/my-profile/payments");
  };

  const onError = (message) => {
    setIsloading(false);
    setError(message);
  };

  const formik = useFormik({
    initialValues: { transfer_amount: "0.0" },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setError("");
      setIsloading(true);
      setTransferAmountValue(values.transfer_amount);
      const data = new FormData();
      data.append("amount", values.transfer_amount);
      await dispatch(actions.transferAmount(data, onTransferAmountSuccess, onError));
    },
  });

  useEffect(() => {
    if (!show) {
      formik.resetForm();
      setError(null);
    }
    if (authStatus) {
      formik.setFieldValue('transfer_amount', transferAmountValue);
      formik.submitForm();
    }
  }, [show, authStatus]);

  const onFocusHandler = () => {
    if (formik.values.transfer_amount === '0.0') {
        formik.setFieldValue('transfer_amount', '');
    }
  };

  const onBlurHandler = () => {
      if (formik.values.transfer_amount === '') {
          formik.setFieldValue('transfer_amount', '0.0');
      }
  };

  const onClickHandler = () => {
      if (formik.values.transfer_amount === '0.0') {
          formik.setFieldValue('transfer_amount', '');
      }
  };

  const onDisconnectHandler = () => {
    setShowDisconnectModal(true);
  };

  const onSoftDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      const response = await dispatch(actions.disconnectStripeAccount('temporary'));
      setShowDisconnectModal(false);
      
      if (response && response.status) {
        toastHandler(response.message || "Stripe account disconnected temporarily", "success", toastId);
        onHide();
      } else {
        toastHandler(response?.message || "Failed to disconnect Stripe account", "error", toastId);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to disconnect Stripe account";
      toastHandler(errorMessage, "error", toastId);
    } finally {
      setIsDisconnecting(false);
    }
  };

  const onPermanentDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      const response = await dispatch(actions.disconnectStripeAccount('permanent'));
      setShowDisconnectModal(false);
      
      if (response && response.status) {
        toastHandler(response.message || "Stripe account disconnected permanently", "success", toastId);
        onHide();
      } else {
        toastHandler(response?.message || "Failed to disconnect Stripe account", "error", toastId);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to disconnect Stripe account";
      toastHandler(errorMessage, "error", toastId);
    } finally {
      setIsDisconnecting(false);
    }
  };

  const onStripeAccountSetup = async () => {
    setIsSettingUpStripe(true);
    
    const onSuccess = (response) => {
      if (response && response.data && response.data.url) {
        window.location.href = response.data.url;
        toastHandler("Redirecting to Stripe account setup...", "success", toastId);
        setIsSettingUpStripe(false);
      } else if (response && response.data && (response.data.status === true || response.status === true)) {
        toastHandler(response.message || "Stripe account setup successful", "success", toastId);
        dispatch(actions.getStripeAccountInfo(
          (infoResponse) => {
            setIsSettingUpStripe(false);
          },
          (error) => {
            console.error('Failed to refresh Stripe account info:', error);
            setIsSettingUpStripe(false);
          }
        ));
      } else {
        toastHandler(response?.message || "Failed to setup Stripe account", "error", toastId);
        setIsSettingUpStripe(false);
      }
    };

    const onError = (errorMessage) => {
      toastHandler(errorMessage || "Failed to setup Stripe account", "error", toastId);
      setIsSettingUpStripe(false);
    };

    try {
      await dispatch(actions.getStripeAccountSetup(onSuccess, onError));
    } catch (error) {
      toastHandler("An unexpected error occurred", "error", toastId);
      setIsSettingUpStripe(false);
    }
  };

  const onStripeDashboardClick = async () => {
    setIsLoadingStripeDashboard(true);
    
    const onSuccess = (response) => {
      if (response && response.data && response.data.url) {
        window.open(response.data.url, '_blank');
        toastHandler("Redirecting to Stripe dashboard...", "success", toastId);
      } else {
        toastHandler(response?.message || "Failed to generate Stripe dashboard link", "error", toastId);
      }
      setIsLoadingStripeDashboard(false);
    };

    const onError = (errorMessage) => {
      toastHandler(errorMessage || "Failed to access Stripe dashboard", "error", toastId);
      setIsLoadingStripeDashboard(false);
    };

    try {
      await dispatch(actions.getStripeAccountLogin(onSuccess, onError));
    } catch (error) {
      toastHandler("An unexpected error occurred", "error", toastId);
      setIsLoadingStripeDashboard(false);
    }
  };


  const isAccountActive = stripeAccountInfo?.account_status === 'active';
  const isAccountInactive = stripeAccountInfo?.account_status === 'inactive';

  return (
    <>
      <Modal
        show={show}
        onHide={handleModalClose}
        className="transfer-model transfer-amount-model"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body>
          {isLoadingStripeInfo ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
              <div className="text-center">
                <Spinner />
                <p className="mt-3">Loading Stripe account information...</p>
              </div>
            </div>
          ) : (
            <>
              <h3>Transfer Amount</h3>
              <p>Your amount will be transferred into following connected stripe account</p>
          
              {isAccountActive && (
                <div 
                  className="stripe-dashboard" 
                  onClick={onStripeDashboardClick}
                  style={{
                    cursor: isLoadingStripeDashboard ? 'not-allowed' : 'pointer',
                    opacity: isLoadingStripeDashboard ? 0.6 : 1,
                    textDecoration: 'none'
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="17.641" height="12.272" viewBox="0 0 17.641 12.272">
                    <path id="dashboard-svgrepo-com" d="M7.706,289.169a23.339,23.339,0,0,0,.767,3.535.978.978,0,1,0,1.729-.916A23.5,23.5,0,0,0,7.706,289.169Zm-.4-1.8a35.2,35.2,0,0,1,3.816,3.938,2.017,2.017,0,0,1-3.563,1.889,35.239,35.239,0,0,1-1.118-5.368.519.519,0,0,1,.865-.459Zm1.561-5.374a8.831,8.831,0,0,1,8.576,10.9l-.1.4H12.527v-1.038h3.99a7.784,7.784,0,1,0-15.435-1.434,7.9,7.9,0,0,0,.13,1.434H5.69v1.038H.383l-.1-.4a8.834,8.834,0,0,1,8.577-10.9Zm4.985,3.59.734.734-1.036,1.036-.734-.734Zm-9.971,0,1.036,1.036-.734.734-1.036-1.036.734-.734Zm5.5-1.85v1.465H8.345v-1.465H9.383Z" transform="translate(-0.044 -281.99)" fill="#0566c8" fill-rule="evenodd"/>
                  </svg>
                  <span>
                    {isLoadingStripeDashboard ? 'Loading...' : 'Stripe Dashboard'}
                  </span>
                </div>
              )}

              <div className="stripe-box mt-4">
                <div>
                  <div className="statusType-box">
                    <div 
                      className={`status ${isAccountActive ? 'active' : 'in-active'} ${isAccountInactive ? 'clickable' : ''}`}
                      onClick={isAccountInactive ? onStripeAccountSetup : undefined}
                      style={{ 
                        cursor: isAccountInactive ? 'pointer' : 'default',
                        position: 'relative'
                      }}
                    >
                      {isAccountActive ? 'Active' : (isSettingUpStripe ? 'Loading...' : 'Inactive')}
                      {isAccountInactive && !isSettingUpStripe && (
                        <span className="inactive-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" width="10.746" height="11.427" viewBox="0 0 10.746 11.427">
                            <g id="external-link" transform="translate(-2 -4.293)">
                              <path id="Path_3539" data-name="Path 3539" d="M11.176,9.816v3.271a1.09,1.09,0,0,1-1.09,1.09h-6A1.09,1.09,0,0,1,3,13.086v-6A1.09,1.09,0,0,1,4.09,6H7.361" transform="translate(-0.5 1.044)" fill="none" stroke="#cb2027" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"/>
                              <path id="Path_3540" data-name="Path 3540" d="M15,3h4.088V7.088" transform="translate(-7.049 2)" fill="none" stroke="#cb2027" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"/>
                              <line id="Line_63" data-name="Line 63" y1="6.473" x2="6.132" transform="translate(5.907 5)" fill="none" stroke="#cb2027" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"/>
                            </g>
                          </svg>
                        </span>
                      )}
                    </div>
                    <p className="type">Type: <span>{stripeAccountInfo?.account_type || 'Individual'}</span></p>
                  </div>
                  <div className="emailBox">
                    <p>{stripeAccountInfo?.email || currentUser?.payment_info?.email || amountInfoMail || 'Stripe Email'}</p>
                  </div>
                  <div className="countryLogo-box">
                    <div className="country">{stripeAccountInfo?.country_code || 'US'}</div>
                    <div className="logo">
                      <svg id="stripe-svgrepo-com" xmlns="http://www.w3.org/2000/svg" width="51.775" height="21.544" viewBox="0 0 51.775 21.544">
                        <path id="Path_3537" data-name="Path 3537" d="M3.639,8.442c0-.561.46-.777,1.222-.777a8.022,8.022,0,0,1,3.567.92V5.206a9.484,9.484,0,0,0-3.567-.662C1.942,4.545,0,6.069,0,8.615c0,3.969,5.465,3.337,5.465,5.048,0,.662-.575.877-1.381.877A8.952,8.952,0,0,1,.158,13.39v3.423a9.969,9.969,0,0,0,3.926.82c2.991,0,5.048-1.481,5.048-4.056C9.118,9.291,3.639,10.053,3.639,8.442Zm9.722-6.774-3.509.748-.014,11.52a3.614,3.614,0,0,0,3.725,3.7,5.554,5.554,0,0,0,2.517-.475v-2.92c-.46.187-2.733.849-2.733-1.28V7.853h2.733V4.789H13.346Zm7.191,4.185-.23-1.064H17.215V17.373h3.6V8.845A2.536,2.536,0,0,1,23.543,8.1V4.789A2.441,2.441,0,0,0,20.552,5.853Zm3.869-1.064h3.61V17.373h-3.61Zm0-1.093,3.61-.777V0l-3.61.762Zm11.117.849a4.063,4.063,0,0,0-2.819,1.122l-.187-.892H29.368V21.544l3.6-.762.014-4.07a4.05,4.05,0,0,0,2.546.906c2.574,0,4.919-2.071,4.919-6.63C40.428,6.817,38.055,4.545,35.538,4.545Zm-.863,9.909a2.15,2.15,0,0,1-1.7-.676l-.014-5.336a2.141,2.141,0,0,1,1.712-.7c1.309,0,2.215,1.467,2.215,3.351C36.89,13.016,36,14.454,34.675,14.454Zm17.1-3.322c0-3.682-1.783-6.587-5.192-6.587S41.089,7.45,41.089,11.1c0,4.329,2.445,6.515,5.954,6.515a8.019,8.019,0,0,0,3.984-.935V13.807A7.636,7.636,0,0,1,47.5,14.6c-1.4,0-2.632-.489-2.79-2.186h7.033C51.747,12.225,51.775,11.477,51.775,11.132Zm-7.1-1.366c0-1.625.992-2.3,1.9-2.3.877,0,1.812.676,1.812,2.3Z" fill="#6772e5"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <button type="" className="btn disconnect" onClick={onDisconnectHandler}>
                <svg xmlns="http://www.w3.org/2000/svg" width="11.844" height="12.27" viewBox="0 0 11.844 12.27">
                  <g id="Group_9851" data-name="Group 9851" transform="translate(0 -70)">
                    <path id="形状" d="M6.01,11.816a.625.625,0,0,1,.626.542l0,.072v1.227a.632.632,0,0,1-1.257.072l0-.072V12.429A.622.622,0,0,1,6.01,11.816Zm5.2-4.983a.644.644,0,0,1,.833-.051l.059.051,1.338,1.3a3.309,3.309,0,0,1,0,4.772,3.54,3.54,0,0,1-4.791.107l-.115-.107-1.338-1.3a.6.6,0,0,1,0-.868.644.644,0,0,1,.833-.051l.059.051,1.338,1.3a2.252,2.252,0,0,0,3.122,0,2.105,2.105,0,0,0,.089-2.945L12.546,9,11.209,7.7A.6.6,0,0,1,11.209,6.833ZM4.118,10.589a.613.613,0,0,1,.074,1.223l-.074,0H3.487a.613.613,0,0,1-.074-1.223l.074,0ZM6.749,6.4a.644.644,0,0,1,.833-.051l.059.051L10.316,9a.6.6,0,0,1,0,.868.644.644,0,0,1-.833.051L9.425,9.87l-2.676-2.6A.6.6,0,0,1,6.749,6.4ZM3.627,3.363a3.54,3.54,0,0,1,4.791-.107l.115.107,1.338,1.3a.6.6,0,0,1,0,.868.644.644,0,0,1-.833.051l-.059-.051-1.338-1.3a2.252,2.252,0,0,0-3.122,0A2.105,2.105,0,0,0,4.43,7.175l.089.092,1.338,1.3a.6.6,0,0,1,0,.868.644.644,0,0,1-.833.051l-.059-.051-1.338-1.3A3.31,3.31,0,0,1,3.627,3.363Zm9.952,1.091a.614.614,0,1,1,0,1.227h-.631a.614.614,0,1,1,0-1.227ZM11.056,2a.625.625,0,0,1,.626.542l0,.072V3.841a.632.632,0,0,1-1.257.072l0-.072V2.614A.622.622,0,0,1,11.056,2Z" transform="translate(-2.611 68)" fill="#cb2027"/>
                  </g>
                </svg>
                Disconnect
              </button>
              <hr/>

              <form onSubmit={formik.handleSubmit}>
                <p>Enter Amount you want to transfer</p>
                <div className="form-floating mb-3">
                  {
                    <span style={{
                        position: "absolute",
                        left: 14,
                        top: 20,
                        color: "#000",
                        fontSize: 14,
                        zIndex: 2
                    }}>$</span>
                  }
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Amount"
                    name="transfer_amount"
                    value={formik.values.transfer_amount}
                    onFocus={onFocusHandler}
                    onBlur={onBlurHandler}
                    onClick={onClickHandler}
                    onChange={formik.handleChange}
                    style={{ paddingLeft: 24 }}
                    disabled={isAccountInactive}
                  />
                  <label for="mobilenumber">Amount</label>
                  {formik?.touched?.transfer_amount &&
                    formik?.errors?.transfer_amount && (
                      <div style={{ color: "red" }}>
                        {formik?.errors?.transfer_amount}
                      </div>
                    )}
                </div>
                {formik.values.transfer_amount && 
                  formik.values.transfer_amount > 0 && 
                  !isAccountInactive ? (
                  <button 
                    type="submit" 
                    className="btn text-white w-100 btn-primary"
                    disabled={isLoading}
                  >
                    {isLoading ? <Spinner /> : `TRANSFER $${formik.values.transfer_amount}`}
                  </button>
                ) : null}
                {error && (
                  <Form.Control.Feedback type="d-block invalid">
                    {error}
                  </Form.Control.Feedback>
                )}
              </form>
            </>
          )}
        </Modal.Body>
      </Modal>
      <StripeDisconnectModal
        show={showDisconnectModal}
        onHide={() => setShowDisconnectModal(false)}
        onSoftDisconnect={onSoftDisconnect}
        onPermanentDisconnect={onPermanentDisconnect}
        isLoading={isDisconnecting}
      />
    </>
  );
}
